import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Breadcrumb from '../components/breadcrumb';
import SearchBar from '../components/contractor-management/SearchBar';
import { getContractorManagementAccess } from '../config/roleAccess';
import MechanicalContractorTable from '../components/mechanical-contractor-management/MechanicalContractorTable';
import MechanicalContractorDialogs from '../components/mechanical-contractor-management/MechanicalContractorDialogs';
import { SortConfig, MechanicalContractor } from '../components/mechanical-contractor-management/types';
import { MechanicalContractorFormData, MechanicalContractorFormErrors, initialMechanicalContractorFormData, initialMechanicalContractorFormErrors } from '../components/mechanical-contractor-management/dialogTypes';
import { validateStep1, validateStep2 } from '../components/mechanical-contractor-management/validation';
import { ContractorService } from '../services/contractor.service';
import { lookupService } from '../services/lookups.service';
import { authService } from '../services/auth.service';
import { getCoordinatesFromAddress } from '../components/contractor-management/utils';

const MechanicalContractorManagement: React.FC = (): JSX.Element => {
  const user = authService.getUser();
  const contractorAccess = getContractorManagementAccess(user?.role || '');
  const navigate = useNavigate();
  const [contractors, setContractors] = useState<MechanicalContractor[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: 'name',
    direction: null,
  });

  const pageSize = 10;
  const [totalItems, setTotalItems] = useState(0);

  // Dialog states
  const [showAddMechanicalContractorDialog, setShowAddMechanicalContractorDialog] = useState(false);
  const [showSaveConfirmDialog, setShowSaveConfirmDialog] = useState(false);
  const [showCloseConfirmDialog, setShowCloseConfirmDialog] = useState(false);
  const [editFormData, setEditFormData] = useState<any>(null);
  const [pendingCloseAction, setPendingCloseAction] = useState<null | (() => void)>(null);

  // Form states
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<MechanicalContractorFormData>(initialMechanicalContractorFormData);
  const [errors, setErrors] = useState<MechanicalContractorFormErrors>(initialMechanicalContractorFormErrors);
  const [hasShownSimilarNameWarning, setHasShownSimilarNameWarning] = useState(false);

  // Province options
  const [provinceOptions, setProvinceOptions] = useState<any[]>([]);
  const [isLoadingProvinces, setIsLoadingProvinces] = useState(false);

  // Deactivation eligibility
  const [canDeactivate, setCanDeactivate] = useState<boolean>(true);
  const [deactivationReason, setDeactivationReason] = useState<string>('');

  // Refs
  const addMechanicalContractorDialogContentRef = useRef<HTMLDivElement>(null);

  // Function to fetch contractors from API
  const fetchMechanicalContractors = async () => {
    setLoading(true);
    try {
      const filterParams = {
        search: searchTerm,
        page: currentPage,
        limit: pageSize,
        sortBy: sortConfig.key === 'name' ? 'contractor' : sortConfig.key,
        sortOrder: sortConfig.direction || 'asc',
      };

      const response = await ContractorService.getContractorsWithFilters(filterParams);
      // Map contractor data to mechanical contractor format
      const mappedContractors: MechanicalContractor[] = response.data.map((contractor: any) => ({
        id: contractor.id,
        name: contractor.name,
        address: contractor.address || [],
        status: contractor.status?.toLowerCase() === 'active' ? 'active' : 'inactive',
        contractorTrades: contractor.contractorTrades || []
      }));

      setContractors(mappedContractors);
      setTotalItems(response.totalCount);
    } catch (error) {
      console.error('Error fetching contractors:', error);
      setContractors([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key: string, direction: 'asc' | 'desc' | null) => {
    setSortConfig({ key, direction });
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleRowClick = (contractor: MechanicalContractor) => {
    navigate(`/mechanical-contractor-management/details/${contractor.id}`);
  };

  // Function to reset form data to initial state
  const resetFormData = () => {
    setFormData(initialMechanicalContractorFormData);
    setCurrentStep(1);
    setErrors(initialMechanicalContractorFormErrors);
  };

  const handleAddContractor = async () => {
    resetFormData();
    setShowAddMechanicalContractorDialog(true);

    try {
      const [localsData, tradesData] = await Promise.all([
        lookupService.getLocals(),
        lookupService.getTrades()
      ]);
      const uaTradeIds = tradesData.filter(t => t.category === 'UA').map(t => t.id);

      setFormData(prev => ({
        ...prev,
        localTradeSelections: localsData.map(local => ({
          localUnionId: local.id,
          localUnionName: `${local.code} - Local ${local.unionNumber}`,
          tradeIds: uaTradeIds,
          unionType: 'union' as const
        }))
      }));
    } catch (error) {
      console.error('Error preselecting local unions and trades:', error);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Clear related errors when user makes changes
    if (errors[name as keyof MechanicalContractorFormErrors]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubOfficeChange = (index: number, field: string, value: string) => {
    const updatedSubOffices = [...formData.subOffices];
    updatedSubOffices[index] = {
      ...updatedSubOffices[index],
      [field]: value
    };
    setFormData(prev => ({
      ...prev,
      subOffices: updatedSubOffices
    }));
  };

  const handleBack = () => {
    setCurrentStep(prev => prev - 1);
  };

  const handleNext = () => {
    if (currentStep === 1) {
      const isValid = validateStep1(formData, setErrors);
      if (isValid) {
        setCurrentStep(2);
      }
    } else if (currentStep === 2) {
      // Create a temporary errors object to capture validation messages
      const tempErrors: MechanicalContractorFormErrors = { ...initialMechanicalContractorFormErrors };
      const isValid = validateStep2(formData, (newErrors) => {
        Object.assign(tempErrors, newErrors);
        setErrors(newErrors);
      }, provinceOptions);

      if (isValid) {
        // Submit the form
        setShowSaveConfirmDialog(true);
      } else {
        // Show specific validation error in toast
        if (tempErrors.address) {
          toast.error(tempErrors.address);
        } else if (tempErrors.postalCode) {
          toast.error(tempErrors.postalCode);
        } else {
          toast.error('Please fix the validation errors before saving');
        }
      }
    }
  };

  const confirmSave = async (ignoreSimilarNameWarning: boolean = false) => {

    setIsSubmitting(true);
    setShowSaveConfirmDialog(false);

    try {
      // Update coordinates for main address before saving (only if all required fields are present)
      if (formData.address && formData.city && formData.province && formData.postalCode) {
        const mainCoordinates = await getCoordinatesFromAddress(
          formData.address,
          formData.city,
          formData.province,
          formData.postalCode
        );
        if (mainCoordinates) {
          formData.coordinates = mainCoordinates;
        }
      }

      // Update coordinates for sub offices before saving
      for (let i = 0; i < formData.subOffices.length; i++) {
        const office = formData.subOffices[i];
        if (office.address && office.city && office.province && office.postalCode) {
          const coordinates = await getCoordinatesFromAddress(
            office.address,
            office.city,
            office.province,
            office.postalCode
          );
          if (coordinates) {
            formData.subOffices[i].coordinates = coordinates;
          }
        }
      }

      // Format data for API
      const contractorTrades = formData.localTradeSelections.flatMap((selection: any) => {
        // If no trades selected for this local, create one entry with just the local and union type
        if (selection.tradeIds.length === 0) {
          return [{
            localUnionId: selection.localUnionId,
            unionType: selection.unionType
          }];
        }

        // Create one entry for each trade under this local
        return selection.tradeIds.map((tradeId: string) => ({
          trade: tradeId,
          localUnionId: selection.localUnionId,
          unionType: selection.unionType
        }));
      });

      // Format addresses - only include if data is provided
      const addresses = [];

      // Include head office only if at least one field is provided
      if (formData.address || formData.city || formData.province || formData.postalCode) {
        addresses.push({
          addressType: 'head-office',
          address: formData.address || null,
          city: formData.city || null,
          province: formData.province || null,
          postalCode: formData.postalCode || null,
          coordinates: (formData.coordinates && Array.isArray(formData.coordinates) && formData.coordinates.length > 0) ? formData.coordinates : null
        });
      }

      // Add valid sub offices
      const validSubOffices = formData.subOffices.filter(office =>
        office.address || office.city || office.province || office.postalCode
      );

      addresses.push(...validSubOffices.map(office => ({
        addressType: 'sub-office',
        address: office.address || null,
        city: office.city || null,
        province: office.province || null,
        postalCode: office.postalCode || null,
        coordinates: (office.coordinates && Array.isArray(office.coordinates) && office.coordinates.length > 0) ? office.coordinates : null
      })));

      const apiData: any = {
        name: formData.name,
        status: formData.status,
        contractorTrades: contractorTrades,
        address: addresses
      };

      // If we've already shown the warning, add the flag to bypass it
      if (hasShownSimilarNameWarning) {
        apiData.ignoreSimilarNameWarning = true;
      }

      if (editFormData) {
        // Update existing mechanical contractor
        await ContractorService.updateContractor(editFormData.id, apiData);
        toast.success('Mechanical contractor updated successfully');
      } else {
        // Create new mechanical contractor
        await ContractorService.createContractor(apiData);
        toast.success('Mechanical contractor created successfully');
      }

      // Close dialogs and refresh data
      setShowSaveConfirmDialog(false);
      setShowAddMechanicalContractorDialog(false);
      setEditFormData(null);
      resetFormData();
      setHasShownSimilarNameWarning(false); // Reset the warning flag
      await fetchMechanicalContractors();
    } catch (error: any) {
      console.log('=== Error caught ===');
      console.log('error:', error);
      console.log('error.message:', error.message);
      console.log('error.response?.data:', error.response?.data);

      // Get the error message from either error.message or error.response.data.message
      const errorMessage = error.message || error.response?.data?.message || '';
      console.log('errorMessage:', errorMessage);

      // Check if this is a similar name warning (not a hard error)
      if (errorMessage.includes('similar name already exists') && !hasShownSimilarNameWarning) {
        console.log('Similar name warning detected - blocking creation');
        const existingName = errorMessage.match(/"([^"]+)"/)?.[1] || 'another contractor';

        // Mark that we've shown the warning
        setHasShownSimilarNameWarning(true);

        // Show warning toast and do NOT proceed
        toast.warning(
          `A contractor with a similar name already exists: "${existingName}". Click "Add Mechanical Contractor" again to proceed.`,
          { autoClose: 5000 }
        );

        // Re-open the save confirmation dialog so user can click again
        setShowSaveConfirmDialog(true);
      } else {
        // Regular error - show error toast
        console.log('Regular error, showing toast');

        // Transform technical error messages to user-friendly ones
        let displayMessage = 'Error saving mechanical contractor';

        if (errorMessage) {
          if (errorMessage.includes('coordinates') && errorMessage.includes('should not be empty')) {
            displayMessage = 'Please provide complete address information including street, city, province, and postal code to save the contractor.';
          } else if (errorMessage.includes('coordinates')) {
            displayMessage = 'Invalid address information. Please check that all address fields are filled correctly.';
          } else {
            displayMessage = errorMessage;
          }
        }

        toast.error(displayMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelSave = () => {
    setShowSaveConfirmDialog(false);
  };

  const handleCloseAddMechanicalContractorDialog = () => {
    // Check if form has changes
    const hasChanges = formData.name !== initialMechanicalContractorFormData.name ||
      formData.status !== initialMechanicalContractorFormData.status ||
      formData.address !== initialMechanicalContractorFormData.address ||
      formData.city !== initialMechanicalContractorFormData.city ||
      formData.province !== initialMechanicalContractorFormData.province ||
      formData.postalCode !== initialMechanicalContractorFormData.postalCode ||
      formData.subOffices.length !== initialMechanicalContractorFormData.subOffices.length ||
      formData.localTradeSelections.length !== initialMechanicalContractorFormData.localTradeSelections.length;

    if (!hasChanges) {
      setShowAddMechanicalContractorDialog(false);
      setEditFormData(null);
      resetFormData();
      setHasShownSimilarNameWarning(false);
      return;
    }

    // Show confirmation dialog
    setShowCloseConfirmDialog(true);
    setPendingCloseAction(() => () => {
      setShowAddMechanicalContractorDialog(false);
      setEditFormData(null);
      resetFormData();
      setShowCloseConfirmDialog(false);
      setHasShownSimilarNameWarning(false);
    });
  };

  // Initial load
  useEffect(() => {
    fetchMechanicalContractors();
    // Load provinces
    const loadProvinces = async () => {
      setIsLoadingProvinces(true);
      try {
        const provinces = await lookupService.getProvinces();
        setProvinceOptions(provinces);
      } catch (error) {
        console.error('Error loading provinces:', error);
      } finally {
        setIsLoadingProvinces(false);
      }
    };
    loadProvinces();
  }, []);

  // Refetch when search, sort, or page changes
  useEffect(() => {
    fetchMechanicalContractors();
  }, [searchTerm, sortConfig, currentPage]);

  // Check deactivation eligibility when editing a contractor
  useEffect(() => {
    const checkEligibility = async () => {
      if (editFormData?.id && editFormData.status === 'active') {
        try {
          const eligibility = await ContractorService.checkDeactivationEligibility(editFormData.id);
          setCanDeactivate(eligibility.canDeactivate);
          setDeactivationReason(eligibility.reason || '');
        } catch (error) {
          console.error('Error checking deactivation eligibility:', error);
          setCanDeactivate(true);
          setDeactivationReason('');
        }
      } else {
        setCanDeactivate(true);
        setDeactivationReason('');
      }
    };

    if (showAddMechanicalContractorDialog) {
      checkEligibility();
    }
  }, [editFormData, showAddMechanicalContractorDialog]);

  return (
    <div className="bg-gray-50 p-3 sm:p-4 md:p-6">
      <div className="mb-4 sm:mb-6">
        <Breadcrumb title="Mechanical Contractor Management" />
      </div>

      <div className="bg-white rounded-card shadow overflow-hidden p-3 sm:p-4 mb-4">
        <div className="flex items-center gap-2 w-full sm:w-auto justify-between">
          <SearchBar
            searchTerm={searchTerm}
            handleSearch={handleSearch}
            placeholderText={"Search by Name and Status"}
          />
          <div className="flex items-center gap-2">
            {contractorAccess.addContractor && (
            <button
              onClick={handleAddContractor}
              className="btn-primary text-[24px] sm:text-[16px] rounded-full sm:text-body w-fit sm:w-auto sm:rounded-[8px]"
            >
              <span className=""> + </span>
              <span className="ml-1 hidden sm:inline">
                Add Mechanical Contractor
              </span>
            </button>
            )}
          </div>
        </div>
      </div>

      <MechanicalContractorTable
        loading={loading}
        mechanicalContractors={contractors}
        totalItems={totalItems}
        currentPage={currentPage}
        pageSize={pageSize}
        sortConfig={sortConfig}
        searchTerm={searchTerm}
        handleSearch={handleSearch}
        handleRowClick={handleRowClick}
        handleSort={handleSort}
        setCurrentPage={setCurrentPage}
      />

      <MechanicalContractorDialogs
        showAddMechanicalContractorDialog={showAddMechanicalContractorDialog}
        showSaveConfirmDialog={showSaveConfirmDialog}
        showCloseConfirmDialog={showCloseConfirmDialog}
        isSubmitting={isSubmitting}
        formData={formData}
        errors={errors}
        currentStep={currentStep}
        editFormData={editFormData}
        addMechanicalContractorDialogContentRef={addMechanicalContractorDialogContentRef}
        provinceOptions={provinceOptions}
        isLoadingProvinces={isLoadingProvinces}
        canDeactivate={canDeactivate}
        deactivationReason={deactivationReason}
        handleCloseAddMechanicalContractorDialog={handleCloseAddMechanicalContractorDialog}
        handleInputChange={handleInputChange}
        handleSubOfficeChange={handleSubOfficeChange}
        handleBack={handleBack}
        handleNext={handleNext}
        handleCancelSave={handleCancelSave}
        confirmSave={confirmSave}
        setShowCloseConfirmDialog={setShowCloseConfirmDialog}
        pendingCloseAction={pendingCloseAction}
        setFormData={setFormData}
      />
    </div>
  );
};

export default MechanicalContractorManagement;